/* Spec COG-009 — Admin roles and permissions, shared by the server (the rule) and the screens (they only hide).
   Until COG-008 decides roles for the whole panel, a person's admin role comes from PANEL_ADMIN_ROLES
   ("email:role,email:role"); anyone allowed in the panel without an entry is "admin". */

export const ADMIN_ROLES = ['admin', 'gerencia', 'financeiro', 'eventos', 'relacionamento', 'moderacao', 'programas', 'marketing', 'corporativo', 'leitura'] as const
export type AdminRole = (typeof ADMIN_ROLES)[number]

export const ROLE_LABELS: Record<AdminRole, string> = {
    admin: 'Administrador',
    gerencia: 'Gerência',
    financeiro: 'Financeiro',
    eventos: 'Coordenação de eventos',
    relacionamento: 'Relacionamento',
    moderacao: 'Moderação',
    programas: 'Programas',
    marketing: 'Marketing',
    corporativo: 'Corporativo',
    leitura: 'Somente leitura',
}

export const PERMISSIONS = [
    'fichas.ver', 'fichas.editar',
    'export.basico', 'export.dados_pessoais',
    'recorrencias.ver', 'estorno.pedir', 'estorno.aprovar', 'estorno.aprovar_alto',
    'retencao', 'conciliacao', 'conciliacao.revisar',
    'moderacao', 'salvaguarda.relatar', 'salvaguarda.casos', 'ver_como',
    'cartas', 'inscricoes', 'parceiros', 'embaixadores',
    'marketing', 'indicadores',
    'equipe.ver', 'equipe.gerenciar',
] as const
export type Permission = (typeof PERMISSIONS)[number]

const ALL: readonly Permission[] = PERMISSIONS

/** Everyone can (and must) report a safeguarding concern. */
const BASE: Permission[] = ['salvaguarda.relatar']

export const ROLE_PERMISSIONS: Record<AdminRole, readonly Permission[]> = {
    admin: ALL,
    gerencia: [...BASE, 'fichas.ver', 'export.basico', 'recorrencias.ver', 'estorno.aprovar', 'estorno.aprovar_alto', 'retencao', 'conciliacao.revisar', 'cartas', 'parceiros', 'embaixadores', 'marketing', 'indicadores', 'equipe.ver'],
    financeiro: [...BASE, 'fichas.ver', 'export.basico', 'export.dados_pessoais', 'recorrencias.ver', 'estorno.pedir', 'estorno.aprovar', 'conciliacao', 'indicadores'],
    eventos: [...BASE, 'fichas.ver', 'fichas.editar', 'export.basico', 'parceiros'],
    relacionamento: [...BASE, 'fichas.ver', 'recorrencias.ver', 'estorno.pedir', 'retencao', 'moderacao', 'ver_como', 'cartas'],
    moderacao: [...BASE, 'moderacao', 'cartas'],
    programas: [...BASE, 'moderacao', 'salvaguarda.casos', 'cartas', 'inscricoes', 'parceiros'],
    marketing: [...BASE, 'embaixadores', 'marketing', 'indicadores'],
    corporativo: [...BASE, 'export.basico', 'parceiros', 'indicadores'],
    leitura: [...BASE, 'fichas.ver', 'recorrencias.ver', 'indicadores'],
}

export function can(role: AdminRole, permission: Permission): boolean {
    return ROLE_PERMISSIONS[role].includes(permission)
}

export function isAdminRole(value: string): value is AdminRole {
    return (ADMIN_ROLES as readonly string[]).includes(value)
}

/** "a@x.org:financeiro, b@x.org:gerencia" -> { 'a@x.org': 'financeiro', ... }; unknown roles are dropped. */
export function parseAdminRoles(raw: string | undefined): Record<string, AdminRole> {
    const map: Record<string, AdminRole> = {}
    for (const part of (raw ?? '').split(',')) {
        const [email, role] = part.split(':').map(s => s.trim().toLowerCase())
        if (email && role && isAdminRole(role)) map[email] = role
    }
    return map
}

export function roleOf(email: string, roles: Record<string, AdminRole>): AdminRole {
    return roles[email.trim().toLowerCase()] ?? 'admin'
}
